import { Button } from "@common/components/ui/button"
import { Checkbox } from "@common/components/ui/checkbox"
import {
  GridTableCell,
  GridTableHead,
  GridTableHeader,
  GridTableRow,
} from "@common/components/ui/grid-table"
import { Separator } from "@common/components/ui/separator"
import { cn } from "@common/lib/utils"
import { createLink } from "@tanstack/react-router"
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  useRef,
  useState,
} from "react"
import { BELOW_FILTER_BAR } from "@/components/layout/filter-bar"
import type { ColumnArrange } from "@/hooks/use-column-layout"
import { MIN_WIDTH, REM } from "@/lib/column-widths"
import ArrowDownward from "~icons/material-symbols/arrow-downward"
import ArrowUpward from "~icons/material-symbols/arrow-upward"

export type ListSort<C extends string> = {
  column: C
  direction: "asc" | "desc"
}

export type ListColumns<S extends string> = {
  slots: readonly S[]
  end?: readonly S[]
  muted?: readonly S[]
}

const HEADER = cn(BELOW_FILTER_BAR, "group/header")

const END_HEAD = "[&>button]:flex-row-reverse"

const ARRANGEABLE = "group/head relative"

const RESIZE = cn(
  "absolute inset-y-0 z-10 h-auto w-2 cursor-col-resize touch-none",
  "bg-transparent after:absolute after:inset-y-0 after:left-1/2 after:w-px",
  "after:bg-border after:opacity-0 group-hover/head:after:opacity-100",
)

const DROP = "before:absolute before:inset-y-2 before:w-0.5 before:bg-primary"

const DROP_BEFORE = cn(DROP, "before:-left-px")

const DROP_AFTER = cn(DROP, "before:-right-px")

const ROW = cn(
  "group/row relative min-h-9 border-b-0 text-body-regular",
  "hover:no-underline",
)

const CELL = "min-w-0 gap-2 overflow-hidden py-0.5"

const SORT = cn(
  "min-w-0 gap-1 text-small-regular text-muted-foreground",
  "hover:bg-transparent hover:text-foreground [&_svg]:size-3",
)

const STRUCK = "text-muted-foreground line-through"

const LINK = "truncate text-muted-foreground hover:text-foreground"

const STAMP = "flex min-w-0 items-center gap-2"

const STAMP_TEXT = "truncate text-muted-foreground"

const CHECK_REVEAL = "opacity-0 focus:opacity-100 group-hover/row:opacity-100"

const REVEAL = cn(
  "opacity-0 focus-within:opacity-100 group-hover/row:opacity-100",
  "has-[[data-state=open]]:opacity-100",
)

function slotClass<S extends string>(columns: ListColumns<S>, slot: S): string {
  const first = slot === columns.slots[0]
  return cn(
    first && (slot === "select" ? "pl-3" : "pl-6"),
    slot === columns.slots.at(-1) && "pr-6",
    columns.end?.includes(slot) && "justify-end tabular-nums",
    columns.muted?.includes(slot) && "text-muted-foreground",
  )
}

function titleTruncated(event: React.MouseEvent<HTMLElement>): void {
  const texts =
    event.currentTarget.querySelectorAll<HTMLElement>("[data-truncate]")
  for (const text of texts) {
    text.title =
      text.scrollWidth > text.clientWidth ? (text.textContent ?? "") : ""
  }
}

function ariaSort<S extends string>(
  slot: S,
  sort: ListSort<S> | undefined,
): "ascending" | "descending" | undefined {
  if (sort?.column !== slot) return undefined
  return sort.direction === "asc" ? "ascending" : "descending"
}

function dropClass<S extends string>(
  slots: readonly S[],
  slot: S,
  dragged: S | null,
  target: S | null,
): string | undefined {
  if (dragged === null || target !== slot || dragged === slot) return undefined
  return slots.indexOf(slot) > slots.indexOf(dragged) ? DROP_AFTER : DROP_BEFORE
}

export function ListTableHeader<S extends string>({
  columns,
  sort,
  arrange,
  children,
}: {
  columns: ListColumns<S>
  sort?: ListSort<NoInfer<S>>
  arrange?: ColumnArrange<NoInfer<S>>
  children: (slot: S) => React.ReactNode
}): React.ReactElement {
  const [dragged, setDragged] = useState<S | null>(null)
  const [target, setTarget] = useState<S | null>(null)

  return (
    <GridTableHeader className={HEADER} onMouseEnter={titleTruncated}>
      {columns.slots.map((slot) => {
        const content = children(slot)
        const movable = arrange !== undefined && !arrange.fixed.includes(slot)
        return (
          <GridTableHead
            key={slot}
            aria-sort={ariaSort(slot, sort)}
            className={cn(
              "text-small-regular",
              slotClass(columns, slot),
              columns.end?.includes(slot) && END_HEAD,
              arrange && ARRANGEABLE,
              dropClass(columns.slots, slot, dragged, target),
            )}
            draggable={movable}
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = "move"
              event.dataTransfer.setData("text/plain", slot)
              setDragged(slot)
            }}
            onDragEnd={() => {
              setDragged(null)
              setTarget(null)
            }}
            onDragOver={(event) => {
              if (dragged === null || !movable) return
              event.preventDefault()
              setTarget(slot)
            }}
            onDrop={(event) => {
              if (dragged === null || !movable) return
              event.preventDefault()
              if (dragged !== slot) arrange.onReorder(dragged, slot)
            }}
          >
            {typeof content === "string" ? (
              <ListTableText>{content}</ListTableText>
            ) : (
              content
            )}
            {movable && (
              <ListTableResize
                last={slot === columns.slots.at(-1)}
                saved={arrange.widthOf(slot)}
                onResize={(width) => arrange.onResize(slot, width)}
                onResizeEnd={(width) => arrange.onResizeEnd(slot, width)}
              />
            )}
          </GridTableHead>
        )
      })}
    </GridTableHeader>
  )
}

function ListTableResize({
  last,
  saved,
  onResize,
  onResizeEnd,
}: {
  last: boolean
  saved: number
  onResize: (width: number) => void
  onResizeEnd: (width: number) => void
}): React.ReactElement {
  const start = useRef<{ x: number; width: number } | null>(null)
  const resized = useRef<number | null>(null)
  const [active, setActive] = useState(false)

  return (
    <Separator
      orientation="vertical"
      decorative={false}
      draggable
      className={cn(
        RESIZE,
        last ? "right-0" : "-right-1",
        active && "after:opacity-100",
      )}
      onDragStart={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onPointerDown={(event) => {
        const head = event.currentTarget.parentElement
        if (!head) return
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        start.current = {
          x: event.clientX,
          width: Math.max(head.getBoundingClientRect().width, saved * REM),
        }
        setActive(true)
      }}
      onPointerMove={(event) => {
        if (!start.current) return
        const px = start.current.width + event.clientX - start.current.x
        const width = Math.max(MIN_WIDTH, Math.round((px / REM) * 4) / 4)
        if (width === resized.current) return
        resized.current = width
        onResize(width)
      }}
      onLostPointerCapture={() => {
        const width = resized.current
        start.current = null
        resized.current = null
        setActive(false)
        if (width !== null) onResizeEnd(width)
      }}
    />
  )
}

export function ListTableSort<C extends string>({
  column,
  label,
  sort,
  onSort,
}: {
  column: C
  label: string
  sort: ListSort<C> | undefined
  onSort: (column: C) => void
}): React.ReactElement {
  const direction = sort?.column === column ? sort.direction : undefined

  return (
    <Button
      variant="ghost"
      size="inline"
      className={SORT}
      onClick={() => onSort(column)}
    >
      <ListTableText>{label}</ListTableText>
      {direction === "asc" && <ArrowUpward className="text-foreground" />}
      {direction === "desc" && <ArrowDownward className="text-foreground" />}
    </Button>
  )
}

export const ListTableRow = forwardRef<
  HTMLDivElement,
  Omit<ComponentPropsWithoutRef<typeof GridTableRow>, "className"> & {
    selected?: boolean
    dimmed?: boolean
    compact?: boolean
  }
>(function ListTableRow(
  { selected = false, dimmed = false, compact = false, ...props },
  ref,
) {
  return (
    <GridTableRow
      ref={ref}
      className={cn(
        ROW,
        compact && "min-h-8",
        props.onClick && "cursor-pointer",
        selected && "bg-muted",
        dimmed && "text-muted-foreground",
      )}
      {...props}
      onMouseEnter={(event) => {
        titleTruncated(event)
        props.onMouseEnter?.(event)
      }}
    />
  )
})

export function ListTableCell<S extends string>({
  columns,
  slot,
  reveal = false,
  ...props
}: Omit<ComponentPropsWithoutRef<typeof GridTableCell>, "className"> & {
  columns: ListColumns<S>
  slot: S
  reveal?: boolean
}): React.ReactElement {
  return (
    <GridTableCell
      className={cn(CELL, slotClass(columns, slot), reveal && REVEAL)}
      {...props}
    />
  )
}

export function ListTableText({
  struck = false,
  children,
}: {
  struck?: boolean
  children: React.ReactNode
}): React.ReactElement {
  return (
    <span data-truncate className={cn("truncate", struck && STRUCK)}>
      {children}
    </span>
  )
}

export function ListTableReveal({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  return <span className={cn("ml-auto flex shrink-0", REVEAL)}>{children}</span>
}

export function ListTableStamp({
  avatar,
  title,
  children,
}: {
  avatar: React.ReactNode
  title?: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <span title={title} className={STAMP}>
      {avatar}
      <span data-truncate className={STAMP_TEXT}>
        {children}
      </span>
    </span>
  )
}

export function ListTableCheckbox({
  label,
  checked,
  shown = false,
  onToggle,
}: {
  label: string
  checked: boolean
  shown?: boolean
  onToggle: (range: boolean) => void
}): React.ReactElement {
  return (
    <Checkbox
      aria-label={label}
      checked={checked}
      className={cn(!checked && !shown && CHECK_REVEAL)}
      onClick={(event) => onToggle(event.shiftKey)}
    />
  )
}

const ListTableAnchor = forwardRef<
  HTMLAnchorElement,
  ComponentPropsWithoutRef<"a">
>(function ListTableAnchor({ className, ...props }, ref) {
  return (
    <a ref={ref} data-truncate className={cn(LINK, className)} {...props} />
  )
})

export const ListTableLink = createLink(ListTableAnchor)
